import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Button, Field, buttonClassName } from "@evolua/ui";
import { AuthApiError } from "../../auth/auth-api.js";
import {
  sanitizeReturnUrl,
  shouldRedirectAuthenticatedAwayFromAuth,
} from "../../auth/auth-redirect.js";
import { useAuth } from "../../auth/AuthContext.js";
import { getAuthMode } from "../../auth/config.js";
import { AuthLayout } from "./AuthLayout.js";

export function LoginPage() {
  const mode = getAuthMode();
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnUrl = sanitizeReturnUrl(params.get("returnUrl"));
  const reason = params.get("reason");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (mode === "demo") {
    return (
      <AuthLayout
        title="Modo demo"
        lead="VITE_AUTH_MODE=demo: não há login real. Use o seletor de perfil no portal."
      >
        <Link to="/" className={buttonClassName({ variant: "primary" })}>
          Ir ao portal
        </Link>
      </AuthLayout>
    );
  }

  if (
    auth.status === "authenticated" &&
    shouldRedirectAuthenticatedAwayFromAuth("/login")
  ) {
    return <Navigate to={returnUrl} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await auth.login(email.trim(), password);
      if (result.kind === "challenge") {
        const name = result.challenge.challengeName;
        if (name === "MFA_SETUP") {
          navigate("/mfa/setup", { replace: true });
        } else {
          navigate("/mfa", { replace: true });
        }
        return;
      }
      auth.clearSessionExpired();
      navigate(returnUrl, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === "TIMEOUT" || err.code === "NETWORK_ERROR" || err.status === 0) {
          setError(
            err.detail ||
              "A API pode estar iniciando (Render free). Aguarde ~30–60s e tente de novo.",
          );
        } else if (err.status === 503) {
          setError("Autenticação indisponível (AUTH_MODE=disabled no API).");
        } else if (err.status === 429) {
          setError(err.detail || "Muitas tentativas. Aguarde e tente de novo.");
        } else {
          setError(err.detail || "Não foi possível entrar. Verifique e-mail e senha.");
        }
      } else {
        setError(
          "A API pode estar iniciando (Render free). Aguarde ~30–60s e tente de novo.",
        );
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title="Entrar"
      lead="Acesse o portal Evolua HCM com sua conta corporativa."
    >
      {(reason === "expired" || auth.sessionExpired) && (
        <Alert tone="info">
          Sua sessão expirou. Entre novamente para continuar de onde parou.
        </Alert>
      )}
      <form className="evolua-auth__form" onSubmit={onSubmit}>
        <Field
          label="E-mail"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Senha"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <Button type="submit" loading={pending}>
          Entrar
        </Button>
      </form>
      <div className="evolua-auth__links">
        <Link to="/recuperar-senha">Esqueci a senha</Link>
        <Link to="/mfa">Tenho código MFA</Link>
      </div>
    </AuthLayout>
  );
}
